import React, { useState, useEffect, FormEvent } from 'react';
import { apiClient } from '../../api/client';
import { useEvents } from '../../contexts/EventContext';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT, BG_PAGE, BG_CARD } from './table-management/designTokens';

/**
 * Event Setup — the first thing an org admin does after creating an event.
 *
 * One short form: expected guests, number of tables, seats per table. On save
 * it (1) stores the event's capacity, (2) stores the table config, and (3)
 * generates the tables (auto-creating a default "Main" zone if needed) so the
 * floorplan is immediately populated. Reuses existing endpoints:
 *   PATCH /events/:id              → maxCapacity
 *   PATCH /table-management/config → totalTables, seatsPerTable
 *   POST  /table-management/tables/bulk-generate → creates the tables
 */

interface ConfigResponse {
  id: string;
  totalTables: number;
  seatsPerTable: number;
  warningThreshold: number;
}

interface TableRow {
  id: string;
}

interface BulkResult {
  created: number;
  skipped: number;
  zoneId: string;
  zoneName: string;
}

const styles: Record<string, React.CSSProperties> = {
  wrap: { backgroundColor: BG_PAGE, padding: '0.5rem 0' },
  card: { backgroundColor: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '10px', padding: '1.5rem', maxWidth: 560, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  title: { color: TEXT_DARK, fontSize: '1.2rem', fontWeight: 700, margin: '0 0 0.25rem' },
  sub: { color: TEXT_MUTED, fontSize: '0.88rem', margin: '0 0 1.25rem' },
  field: { display: 'flex', flexDirection: 'column' as const, gap: 4, marginBottom: '1rem' },
  label: { fontSize: '0.8rem', fontWeight: 700, color: TEXT_DARK },
  hint: { fontSize: '0.75rem', color: TEXT_MUTED },
  input: { padding: '0.55rem 0.7rem', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '6px', fontSize: '0.95rem', maxWidth: 180 },
  row: { display: 'flex', gap: '1rem', flexWrap: 'wrap' as const },
  seatSummary: { background: '#f5f7f6', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', padding: '0.75rem 1rem', margin: '0.5rem 0 1.25rem', fontSize: '0.9rem', color: TEXT_DARK },
  btn: { padding: '0.6rem 1.2rem', backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer' },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  error: { color: '#b91c1c', background: '#fef2f2', padding: '0.6rem 0.9rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.85rem' },
  success: { color: '#166534', background: '#ecfdf5', padding: '0.6rem 0.9rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.85rem' },
  warn: { color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', padding: '0.6rem 0.9rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.85rem' },
};

export function EventSetupPanel() {
  const { selectedEventId, selectedEvent, refresh } = useEvents();

  const [expectedGuests, setExpectedGuests] = useState('');
  const [totalTables, setTotalTables] = useState('');
  const [seatsPerTable, setSeatsPerTable] = useState('');
  const [existingTableCount, setExistingTableCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [config, tables] = await Promise.all([
          apiClient<ConfigResponse>('/table-management/config').catch(() => null),
          apiClient<TableRow[]>('/table-management/tables').catch(() => [] as TableRow[]),
        ]);
        if (cancelled) return;
        if (config) {
          setTotalTables(String(config.totalTables));
          setSeatsPerTable(String(config.seatsPerTable));
        }
        setExistingTableCount(Array.isArray(tables) ? tables.length : 0);
        // Prefill expected guests from the selected event's capacity if known.
        const cap = (selectedEvent as any)?.maxCapacity;
        if (cap && cap > 0) setExpectedGuests(String(cap));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedEventId, selectedEvent]);

  const nTables = parseInt(totalTables, 10) || 0;
  const nSeats = parseInt(seatsPerTable, 10) || 0;
  const totalSeats = nTables * nSeats;
  const hasExistingTables = (existingTableCount ?? 0) > 0;

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedEventId) {
      setError('No event selected. Pick an event from the Active event picker first.');
      return;
    }
    const guests = parseInt(expectedGuests, 10);
    if (!guests || guests < 1) { setError('Enter the number of guests you expect.'); return; }
    if (nTables < 1) { setError('Enter the number of tables you need.'); return; }
    if (nSeats < 1) { setError('Enter the seats per table.'); return; }

    setSaving(true);
    try {
      // 1. Store expected guests on the event.
      await apiClient(`/events/${selectedEventId}`, { method: 'PATCH', body: { maxCapacity: guests } });
      // 2. Store the table config.
      await apiClient('/table-management/config', { method: 'PATCH', body: { totalTables: nTables, seatsPerTable: nSeats } });
      // 3. Generate the tables.
      const result = await apiClient<BulkResult>('/table-management/tables/bulk-generate', {
        method: 'POST',
        body: { totalTables: nTables, seatsPerTable: nSeats },
      });

      const parts: string[] = [];
      parts.push(`Saved: ${guests} expected guests.`);
      if (result.created > 0) {
        parts.push(`Created ${result.created} table${result.created === 1 ? '' : 's'} (${nSeats} seats each) in “${result.zoneName}”.`);
      }
      if (result.skipped > 0) {
        parts.push(`${result.skipped} table${result.skipped === 1 ? '' : 's'} already existed and ${result.skipped === 1 ? 'was' : 'were'} left as-is.`);
      }
      setSuccess(parts.join(' '));
      setExistingTableCount((prev) => (prev ?? 0) + result.created);
      await refresh();
    } catch (err: any) {
      setError(err?.message || 'Could not complete setup. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div style={styles.wrap}><div style={{ color: TEXT_MUTED, padding: '1rem' }}>Loading setup…</div></div>;
  }

  return (
    <div style={styles.wrap}>
      <form style={styles.card} onSubmit={handleSave}>
        <h2 style={styles.title}>Event Setup</h2>
        <p style={styles.sub}>Tell us how many guests you expect and how your room is laid out. We’ll create the tables for you.</p>

        {error && <div style={styles.error}>{error}</div>}
        {success && <div style={styles.success}>{success}</div>}
        {hasExistingTables && !success && (
          <div style={styles.warn}>
            This event already has {existingTableCount} table{existingTableCount === 1 ? '' : 's'}. Generating will only add tables to reach your target number — existing tables are left untouched.
          </div>
        )}

        <div style={styles.field}>
          <label style={styles.label} htmlFor="expectedGuests">Expected guests</label>
          <input
            id="expectedGuests"
            type="number"
            min={1}
            max={9999}
            style={styles.input}
            value={expectedGuests}
            onChange={(e) => setExpectedGuests(e.target.value)}
            placeholder="e.g. 120"
          />
          <span style={styles.hint}>How many people you’re planning for.</span>
        </div>

        <div style={styles.row}>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="totalTables">Number of tables</label>
            <input
              id="totalTables"
              type="number"
              min={1}
              max={200}
              style={styles.input}
              value={totalTables}
              onChange={(e) => setTotalTables(e.target.value)}
              placeholder="e.g. 15"
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="seatsPerTable">Seats per table</label>
            <input
              id="seatsPerTable"
              type="number"
              min={1}
              max={20}
              style={styles.input}
              value={seatsPerTable}
              onChange={(e) => setSeatsPerTable(e.target.value)}
              placeholder="e.g. 8"
            />
          </div>
        </div>

        {nTables > 0 && nSeats > 0 && (
          <div style={styles.seatSummary}>
            That’s <strong>{nTables}</strong> table{nTables === 1 ? '' : 's'} × <strong>{nSeats}</strong> seats = <strong>{totalSeats}</strong> total seats.
          </div>
        )}

        <button type="submit" style={{ ...styles.btn, ...(saving ? styles.btnDisabled : {}) }} disabled={saving}>
          {saving ? 'Setting up…' : 'Save & create tables'}
        </button>
      </form>
    </div>
  );
}
